import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from 'hooks';

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

/** Reachable without a membership — otherwise the redirect below would loop. */
const BILLING_PATH = '/billing';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { token, isMembershipValid } = useAppSelector(state => state.auth);
  const { pathname } = useLocation();

  if (!token) {
    // If not authenticated, redirect to the login page
    return <Navigate to="/login" />;
  }

  if (isMembershipValid === false) {
    // No active membership (never subscribed, trial over, or payment lapsed):
    // the only page they can reach is Billing, where they pick a plan or start
    // the trial. Billing itself must render, or this would redirect to itself.
    if (!pathname.startsWith(BILLING_PATH)) {
      return <Navigate to={BILLING_PATH} replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  if (isMembershipValid === null) {
    // Optionally show a loading indicator if membership status is still being checked
    return <div>Checking membership status...</div>;
  }

  // If authenticated and membership is valid, render the children or the Outlet (for nested routes)
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
